import {
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ExternalProjectImportEntity,
  ExternalProjectImportPreview,
} from '@provision/entity-types';
import { useMemo } from 'react';
import { ProjectPreview } from 'src/components/ImportDtoolsProjectModal/project-preview';
import { ImportCompletedStep } from 'src/components/ImportPortalioProjectModal/import-completed-step';
import {
  IMPORT_MORE,
  usePortalioImport,
} from 'src/components/ImportPortalioProjectModal/portalio-import-context';
import { ModalStep, useModalStep, useModalSteps } from 'src/components/ModalContainer';
import { usePortalioProjectImportPreview } from 'src/graphql/entities/integrations/portalio/hooks/use-portalio-project-import-preview';

export type ProjectsSummaryProps = {
  projects: { id: number; name: string }[];
};

const countEntities = (records: ExternalProjectImportPreview[] | undefined) => {
  const counts = {
    Project: 0,
    Site: 0,
    Client: 0,
  };

  const bucketEntity = (entity: ExternalProjectImportEntity) => {
    counts[entity.entity] += 1;
  };

  (records || []).forEach((record) => {
    record.created?.forEach(bucketEntity);
    record.updated?.forEach(bucketEntity);
  });

  return counts;
};

/** Step 2: what importing the selected proposals would create and update. */
export const ProjectsSummary = ({ projects }: ProjectsSummaryProps) => {
  const { pushStep } = useModalSteps();
  const { popStep } = useModalStep();
  const { importProjects, importing, close } = usePortalioImport();
  const projectIds = projects.map((p) => p.id);
  const projectNamesMap = useMemo(
    () => new Map(projects.map((p) => [p.id, p.name])),
    [projects],
  );
  const { records, loading, errors } = usePortalioProjectImportPreview(projectIds);

  const counts = countEntities(records);

  const handleImport = async () => {
    await importProjects(projectIds);
    const result = await pushStep(<ImportCompletedStep projectIds={projectIds} />);
    if (result === IMPORT_MORE) {
      popStep(IMPORT_MORE);
    }
  };

  const content = (() => {
    if (loading) {
      return (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height="1.5rem" width="40%" />
          <Skeleton variant="rounded" height="5rem" />
          <Skeleton variant="rounded" height="5rem" />
        </Stack>
      );
    }

    if (!records?.length) {
      return (
        <Stack spacing={1}>
          <Typography variant="subtitle2">No preview available</Typography>
          <Typography variant="body2" color="text.secondary">
            Could not generate a preview for the selected projects.
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack spacing={2}>
        {errors?.length ? (
          <Alert severity="warning">
            <AlertTitle sx={{ marginBottom: 0 }}>
              Failed to get following project(s)
            </AlertTitle>
            <List dense sx={{ paddingY: 0 }}>
              {(errors || []).map((e) => (
                <ListItem key={e.projectId}>
                  <ListItemText
                    primary={projectNamesMap.get(e.projectId) ?? e.projectId}
                    secondary={e.error}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        ) : null}
        {(records || []).map((r) => (
          <ProjectPreview {...r} key={r.id} />
        ))}
        <Typography variant="body2" color="text.secondary">
          {`Summary: ${counts.Site} ${counts.Site === 1 ? 'site' : 'sites'}, ${counts.Client} ${
            counts.Client === 1 ? 'client' : 'clients'
          }, ${counts.Project} ${counts.Project === 1 ? 'project' : 'projects'}`}
        </Typography>
      </Stack>
    );
  })();

  return (
    <ModalStep
      title="Confirm import"
      actions={[
        { label: 'Cancel', kind: 'secondary', onClick: close },
        {
          label: errors?.length ? 'Import anyway' : 'Import all',
          kind: 'primary',
          onClick: () => void handleImport(),
          disabled: projectIds.length === 0 || loading,
          loading: importing,
          loadingLabel: 'Importing',
          testId: 'import-portalio-projects-preview-import',
        },
      ]}
    >
      <Stack gap={2} data-testid="import-portalio-projects-preview">
        <Typography variant="body2" color="text.secondary">
          {`Review ${projectIds.length} project(s) to import`}
        </Typography>
        {content}
      </Stack>
    </ModalStep>
  );
};
